/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useState, } from 'react';
import { Button, Tabs, } from 'tea-component';
import { useIntl, history, connect } from 'umi';
import { LANGUAGE_EN } from '@/pages/home/_config/config';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { INavStudioGameMenuItem } from '@home/pages/studio/type';
import { INavStudioItem } from '@home/types/home';
import { getStudioMenuPath } from '@/pages/home/pages/studio/utils';
import { getBasePath } from '@/utils/utils';
import { buriedPoint } from '@home/utils/buriedPoint';
import { PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL } from '@/pages/dashboard/_config/constant';
import './index.scss';

interface IExploreHeaderPorps {
  navStudioList: Array<INavStudioItem>;
  studioLoading: boolean;
};

const ExploreHeader = (props: IExploreHeaderPorps) => {
  const { navStudioList, studioLoading } = props;
  const isHaveStudioDemo = navStudioList?.some((item)=>item?.studio_code?.toLowerCase() === 'studiodemo');
  const studioList = (navStudioList?.length <= 2 && isHaveStudioDemo) ? navStudioList?.filter(item=> item?.studio_code?.toLowerCase() !== 'studiodemo') : navStudioList;

  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const isHide = studioList?.length === 0 || studioLoading;
  const moreStudio = studioList?.length > 1;
  const studioInfo = studioList?.[0] || {};
  const {
    studio_code: studioCode,
    name_cn: nameCn,
    name_en: nameEn,
    title_cn: titleCn,
    title_en: titleEn,
    icon_url: iconRul,
    background_url: bgUrl,
    element_url: elUrl,
    first_game_code_or_unified_id: gameCode,
    first_game_dashboard_list: menuList = [],
    haveHome,
  } = studioInfo || {};

  const { name, title } = useMemo(() => {
    const name = isEn && nameEn ? nameEn : nameCn;
    const title = isEn && titleEn ? titleEn : titleCn;
    return { name, title };
  }, [isEn, nameCn, nameEn, titleCn, titleEn]);

  const tabs = menuList.map((item) => ({
    ...item,
    id: item?.u_id,
    label: isEn && item?.name_en ? item?.name_en : item?.name_cn,
  } as const));

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  const totalPages = useMemo(() => Math.ceil(studioList.length / itemsPerPage), [studioList.length, itemsPerPage]);

  const paginatedStudios = useMemo(() => {
    return studioList?.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  }, [studioList, currentPage]);

  // 设置初始背景为第一个item的background_url
  const [backgroundImage, setBackgroundImage] = useState(paginatedStudios?.[0]?.background_url);
  // 添加状态以追踪悬停的工作室
  const [hoveredStudioCode, setHoveredStudioCode] = useState(paginatedStudios?.[0]?.studio_code);

  useEffect(() => {
    if (paginatedStudios?.length > 0) { 
      // 立即更新背景图片和悬停状态
      setBackgroundImage(paginatedStudios[0].background_url);
      setHoveredStudioCode(paginatedStudios?.[0]?.studio_code);
    }
  }, [paginatedStudios, currentPage]);

  const handleClickMenu = (dashboard: INavStudioGameMenuItem, isTagJump: boolean) => {
    // 有studio首页权限，则跳转studio首页，如果没有首页权限，则走之前的逻辑
    if (haveHome && !isTagJump) {
      history.push(`/studio/${studioCode}/home`);
      return;
    }
  
    const { url, u_id: id, name_en: nameEn, name_cn: nameCn, } = dashboard || {};
    const logObj = {
      menuId: id,
      pageName: nameEn || nameCn,
      extInfo: nameEn || nameCn,
      pagePath: url,
      uid: gameCode || '',
      typeId: 'homePage_pc_v2',
    };
    
    // out path打开系统path
    if (dashboard?.page_type === PAGE_TYPE_OUT_PATH) {
      buriedPoint({ ...logObj, pageId: '101401' });
      window.open(`${getBasePath()}${url}`);
      return;
    }
    // out url打开外部url
    if (dashboard?.page_type === PAGE_TYPE_OUT_URL) {
      buriedPoint({ ...logObj, pageId: '101501' });
      window.open(`${url}`);
      return;
    }
    // display_mode=tab 须带 nameSlug/首个子 tab，与左侧菜单一致
    const path = getStudioMenuPath(dashboard);
    if (!path) return;
    history.push(`/studio/${studioCode}/${gameCode}/${path}`);
  };

  if (isHide) {
    return null;
  }

  if (moreStudio) {
    const moreStudioStyle: React.CSSProperties & Record<'--explore-bg-url', string> = {
      '--explore-bg-url': `url(${backgroundImage})`,
      zIndex: '1',
      position: 'relative',
      transition: 'background-image 0.5s ease-in-out',
    };
    return (
      <div
        className='dbh-explore-header'
        style={moreStudioStyle}
      >
        {currentPage > 0 && (
          <SvgIcon type="arrow-left"
            className='dbh-explore-header-pagination-arrow left'
            onClick={() => setCurrentPage(currentPage - 1)} />
        )}
        <div className='dbh-explore-header-pagination'>
          {paginatedStudios.map((studio) => {
            const { icon_url: iconUrl, name_cn: nameCn, name_en: nameEn, background_url: backgroundUrl, element_url: elUrl, studio_code: studioCode } = studio || {};
            const name = isEn && nameEn ? nameEn : nameCn;

            return (
              <div key={studioCode} className='dbh-explore-header-studio' style={{ zIndex: '10', position: 'relative' }}>
                <div className='dbh-explore-header-studio-item'
                  onMouseEnter={() => {
                    setHoveredStudioCode(studioCode);
                    setBackgroundImage(backgroundUrl);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const { studio_code: studioCode, first_game_code_or_unified_id: gameCode, haveHome } = studio || {};
                    if (haveHome) {
                      history.push(`/studio/${studioCode}/home`);
                      return;
                    }
                    history.push(`/studio/${studioCode}/${gameCode}`);
                  }}
                >
                  <div className='dbh-explore-header-img-more'>
                    <img className='dbh-explore-img-item' src={iconUrl} />
                  </div>
                  <div className='dbh-explore-header-name'>{name}</div>
                  {hoveredStudioCode === studioCode && (
                    <img className={`dbh-explore-header-element-${studioCode} dbh-explore-header-element`} src={elUrl} style={{ pointerEvents: 'none' }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {currentPage < totalPages - 1 && (
          <SvgIcon type="arrow-right"
            className='dbh-explore-header-pagination-arrow right'
            onClick={() => setCurrentPage(currentPage + 1)} />
        )}
      </div>
    );
  }

  const headerStyle: React.CSSProperties & Record<'--explore-bg-url', string> = {
    '--explore-bg-url': `url(${bgUrl})`,
  };

  return <div
    className='dbh-explore-header'
    style={headerStyle}
    onClick={() => handleClickMenu(menuList[0], false)}
  >
    <img src={elUrl} className='dbh-explore-header-bg' />
    <div className='dbh-explore-header-img'>
      <img className='dbh-explore-img' src={iconRul} />
    </div>
    <div className='dbh-explore-header-context'>
      <div className='dbh-explore-context-flex'>
        <div className='dbh-explore-context-flex-left'>
          <div className="dbh-explore-context-flex-title">{name}</div>
          <div className="dbh-explore-context-flex-desc">{title}</div>
        </div>
        <Button className='dbh-explore-context-flex-right' type='primary'>{formatMessage({ id: 'dbh_studio_header_explore' })}</Button>
      </div>
      <div className='dbh-explore-context-tab-container'
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Tabs
          tabs={tabs}
          className={'dbh-explore-context-tab dbh-arrow-navigated-tabs'}
          // 使用空 string 来规避默认选中
          activeId={''}
          tabBarRender={(children, tab) => {
            const menuItem = tab as typeof tab & INavStudioGameMenuItem;
            const { id, icon } = menuItem;
            
            return (
              <div
                className={`dbh-explore-tab-item`}
                key={id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickMenu(menuItem, true);
                }}
              >
                <SvgIcon type={icon} className="dbh-explore-tab-item-img" />
                <SvgIcon type={`action-${icon}`} className="dbh-explore-tab-item-actionimg" />
                {children}
              </div>
            );
          }}
        />
      </div>
    </div>
  </div>;
};

export default connect((state: any) => {
  const { loading, home } = state;
  return {
    navStudioList: home.navStudioList,
    studioLoading: loading.effects['home/getNavMenuList']
  };
})(ExploreHeader);